import { useEffect, useRef, useState } from "react";
import {
  RECENT_SEARCHES_STORAGE_KEY,
  SearchItem,
} from "~/lib/helpers/autoCompleteSearchHelper";
import { convertSearchUrlToLocationPage } from "~/components/blocks/Geosearch/locationGeosearch";
import { stripPlaceIdFromSearchUrl } from "~/lib/helpers/searchPlaceContext";

function migrateRecentSearchUrls(searches: SearchItem[]): SearchItem[] {
  return searches.map((search) => {
    if (!search.urlString) return search;
    const normalized = stripPlaceIdFromSearchUrl(
      search.urlString.startsWith("/") ? search.urlString : `/${search.urlString}`,
    );
    const converted = convertSearchUrlToLocationPage(normalized);
    if (converted === search.urlString) return search;
    return { ...search, urlString: converted };
  });
}

export function useRecentSearches() {
  const [recentSearches, setRecentSearches] = useState<SearchItem[]>([]);

  useEffect(() => {
    const storedSearches = localStorage.getItem(RECENT_SEARCHES_STORAGE_KEY);
    if (storedSearches) {
      const parsed = JSON.parse(storedSearches) as SearchItem[];
      const migrated = migrateRecentSearchUrls(parsed);
      setRecentSearches(migrated);
      if (JSON.stringify(migrated) !== JSON.stringify(parsed)) {
        localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(migrated));
      }
    }
  }, []);

  return { recentSearches, setRecentSearches };
}

function useEventListener(
  eventName: string,
  handler: (event: Event) => void,
  element: EventTarget = window,
) {
  const savedHandler = useRef(handler);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!(element && element.addEventListener)) return;

    const eventListener = (event: Event) => savedHandler.current(event);
    element.addEventListener(eventName, eventListener);

    return () => {
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
}

export default useEventListener;
